import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { MultiSelect } from "@/components/ui";
import { MapPinIcon, CloseIcon } from "@/components/icons";
import { type DocumentSource, type DocumentSortBy } from "@/hooks/use-document-repo";
import {
  DocumentScopePicker,
  type DocumentScope,
  type ScopeChange,
} from "@/components/documents/document-scope-picker";

const ALL_SOURCES: DocumentSource[] = [
  "document",
  "form",
  "task",
  "notice",
  "cromerr",
  "report_form_output",
];

// Exact content_type values the Type filter offers (server does exact match).
const TYPE_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "application/pdf", labelKey: "repo.types.pdf" },
  { value: "image/jpeg", labelKey: "repo.types.jpeg" },
  { value: "image/png", labelKey: "repo.types.png" },
  { value: "text/csv", labelKey: "repo.types.csv" },
  {
    value: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    labelKey: "repo.types.xlsx",
  },
  {
    value: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    labelKey: "repo.types.docx",
  },
];

// The Sort pill options — each maps to one (sort_by, sort_dir) server pair.
const SORT_OPTIONS: { by: DocumentSortBy; dir: "asc" | "desc"; labelKey: string }[] = [
  { by: "created_at", dir: "desc", labelKey: "repo.sort.newest" },
  { by: "created_at", dir: "asc", labelKey: "repo.sort.oldest" },
  { by: "filename", dir: "asc", labelKey: "repo.sort.nameAsc" },
  { by: "filename", dir: "desc", labelKey: "repo.sort.nameDesc" },
  { by: "size_bytes", dir: "desc", labelKey: "repo.sort.largest" },
  { by: "size_bytes", dir: "asc", labelKey: "repo.sort.smallest" },
  { by: "source", dir: "asc", labelKey: "repo.sort.source" },
];

export interface DocumentRepoFilterValues {
  source?: DocumentSource[];
  type?: string;
  created_after?: string;
  created_before?: string;
}

interface DocumentRepoFiltersProps {
  scope: DocumentScope | null;
  onScopeChange: (change: ScopeChange) => void;
  onClearScope: () => void;
  filters: DocumentRepoFilterValues;
  onChange: (filters: DocumentRepoFilterValues) => void;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  onResetAll: () => void;
}

// Slim pill control class matching the mockup `.bar` toolbar (h-8, 12px text,
// rounded-8, light border). One source of truth keeps the Type / Date / Sort
// pills visually identical.
const PILL =
  "h-8 rounded-lg border border-gray-300 bg-white px-2.5 text-xs text-on-surface dark:border-gray-600 dark:bg-gray-700 dark:text-white";

/**
 * The search-first repository toolbar + active-filter chips (mockup 05 `.bar` +
 * `.chips`). Row 1 is the slim pill bar — the omni-scope picker, a Source
 * multiselect, Type + Date pills, and a right-aligned Sort pill. Row 2 (only
 * when a scope or filter is active) renders each active dimension as a removable
 * chip plus "Reset all". The hero filename search lives in the page header, NOT
 * here. NO NAKED TABLES — this bar IS the table's top filter bar.
 */
export function DocumentRepoFilters({
  scope,
  onScopeChange,
  onClearScope,
  filters,
  onChange,
  sorting,
  onSortingChange,
  onResetAll,
}: DocumentRepoFiltersProps) {
  const { t } = useTranslation("documents");

  const sourceOptions = ALL_SOURCES.map((s) => ({ value: s, label: t(`repo.source.${s}`) }));

  const active = sorting[0];
  const sortValue = active ? `${active.id}:${active.desc ? "desc" : "asc"}` : "created_at:desc";

  const hasActiveFilters =
    (filters.source && filters.source.length > 0) ||
    !!filters.type ||
    !!filters.created_after ||
    !!filters.created_before;
  const hasAnything = !!scope || hasActiveFilters;

  const typeLabel = (value: string) => {
    const opt = TYPE_OPTIONS.find((o) => o.value === value);
    return opt ? t(opt.labelKey) : value;
  };
  const sourceLabels = (sources: DocumentSource[]) =>
    sources.map((s) => t(`repo.source.${s}`)).join(", ");

  return (
    <>
      {/* Row 1 — the slim pill toolbar (mockup `.bar`). */}
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-3.5 py-2.5 dark:border-gray-700">
        <DocumentScopePicker value={scope} onChange={onScopeChange} />

        {/* Source multiselect — slim pill trigger. */}
        <div className="w-40 shrink-0 [&_button]:!h-8 [&_button]:!min-h-8 [&_button]:!rounded-lg [&_button]:!px-2.5 [&_button]:!py-0 [&_button]:!text-xs">
          <MultiSelect
            values={filters.source ?? []}
            onChange={(values) =>
              onChange({
                ...filters,
                source: values.length > 0 ? (values as DocumentSource[]) : undefined,
              })
            }
            options={sourceOptions}
            placeholder={t("repo.filters.allSources")}
          />
        </div>

        {/* Type pill-select. */}
        <select
          value={filters.type ?? ""}
          onChange={(e) => onChange({ ...filters, type: e.target.value || undefined })}
          aria-label={t("repo.filters.type")}
          className={`${PILL} shrink-0`}
        >
          <option value="">{t("repo.filters.allTypes")}</option>
          {TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </option>
          ))}
        </select>

        {/* Date range — two slim date pills. */}
        <input
          type="date"
          value={filters.created_after ?? ""}
          onChange={(e) => onChange({ ...filters, created_after: e.target.value || undefined })}
          aria-label={t("repo.filters.dateFrom")}
          title={t("repo.filters.dateFrom")}
          className={`${PILL} shrink-0`}
        />
        <input
          type="date"
          value={filters.created_before ?? ""}
          onChange={(e) => onChange({ ...filters, created_before: e.target.value || undefined })}
          aria-label={t("repo.filters.dateTo")}
          title={t("repo.filters.dateTo")}
          className={`${PILL} shrink-0`}
        />

        {/* Right-aligned Sort pill (mockup `.grow` + Sort). */}
        <select
          value={sortValue}
          onChange={(e) => {
            const [by, dir] = e.target.value.split(":");
            if (by) onSortingChange([{ id: by, desc: dir === "desc" }]);
          }}
          aria-label={t("repo.sort.label")}
          className={`${PILL} ml-auto shrink-0 font-medium`}
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={`${opt.by}:${opt.dir}`} value={`${opt.by}:${opt.dir}`}>
              {t("repo.sort.label")}: {t(opt.labelKey)}
            </option>
          ))}
        </select>
      </div>

      {/* Row 2 — active-scope + filter chips (mockup `.chips`). */}
      {hasAnything && (
        <div
          data-testid="document-chips"
          className="flex flex-wrap items-center gap-1.5 border-b border-gray-100 px-3.5 py-2.5 dark:border-gray-700"
        >
          <span className="mr-0.5 text-[11px] font-semibold text-muted">
            {t("repo.chips.showing")}
          </span>

          {scope && (
            <Chip
              tone="scope"
              icon={<MapPinIcon className="h-3 w-3" />}
              label={
                scope.rolls_up
                  ? `${scope.label || t("repo.selectedNode")} ${t("repo.picker.inclDevices")}`
                  : scope.label || t("repo.selectedNode")
              }
              removeLabel={t("repo.chips.remove", {
                filter: scope.label || t("repo.selectedNode"),
              })}
              onRemove={onClearScope}
            />
          )}

          {filters.source && filters.source.length > 0 && (
            <Chip
              label={t("repo.chips.source", { value: sourceLabels(filters.source) })}
              removeLabel={t("repo.chips.remove", { filter: t("repo.columns.source") })}
              onRemove={() => onChange({ ...filters, source: undefined })}
            />
          )}
          {filters.type && (
            <Chip
              label={t("repo.chips.type", { value: typeLabel(filters.type) })}
              removeLabel={t("repo.chips.remove", { filter: t("repo.filters.type") })}
              onRemove={() => onChange({ ...filters, type: undefined })}
            />
          )}
          {filters.created_after && (
            <Chip
              label={t("repo.chips.from", { value: filters.created_after })}
              removeLabel={t("repo.chips.remove", { filter: t("repo.filters.dateFrom") })}
              onRemove={() => onChange({ ...filters, created_after: undefined })}
            />
          )}
          {filters.created_before && (
            <Chip
              label={t("repo.chips.to", { value: filters.created_before })}
              removeLabel={t("repo.chips.remove", { filter: t("repo.filters.dateTo") })}
              onRemove={() => onChange({ ...filters, created_before: undefined })}
            />
          )}

          <button
            type="button"
            onClick={onResetAll}
            className="ml-auto text-xs text-muted hover:text-on-surface"
          >
            {t("repo.chips.resetAll")}
          </button>
        </div>
      )}
    </>
  );
}

interface ChipProps {
  label: string;
  removeLabel: string;
  onRemove: () => void;
  tone?: "scope" | "filter";
  icon?: ReactNode;
}

/** A removable active-filter chip. The blue `scope` tone marks the picked entity
 *  axis; the violet `filter` tone marks an orthogonal filter (mockup `.chip`). */
function Chip({ label, removeLabel, onRemove, tone = "filter", icon }: ChipProps) {
  const toneClass =
    tone === "scope"
      ? "border-primary/30 bg-primary/10 text-primary"
      : "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900/40 dark:bg-violet-900/20 dark:text-violet-300";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border py-1 pl-2.5 pr-1.5 text-[11.5px] font-semibold ${toneClass}`}
    >
      {icon}
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full opacity-70 hover:opacity-100"
      >
        <CloseIcon className="h-2.5 w-2.5" />
      </button>
    </span>
  );
}
